import { memo, useCallback, type ChangeEvent, type FC } from "react";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { CompanyPromotionDateOperator } from "@/generated/company-search";
import { useCompanyFilterActions, useCompanyFilterSlice } from "../../hooks";
import { OPERATOR_LABELS } from "./operators";
import type { PromotionDateSlice } from "./slice";

export const PromotionDateFilter: FC = memo(() => {
  const slice = useCompanyFilterSlice("promotionDate") as PromotionDateSlice;
  const { setSlice } = useCompanyFilterActions();

  const handleOperatorChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const operator = event.target.value as CompanyPromotionDateOperator;
      setSlice("promotionDate", (prev) => ({ ...prev, operator }));
    },
    [setSlice]
  );

  const handleDateChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const date = event.target.value || null;
      setSlice("promotionDate", (prev) => ({ ...prev, date }));
    },
    [setSlice]
  );

  return (
    <Stack spacing={1.5} data-testid="promotion-date-filter">
      <Typography variant="body2" color="text.secondary">
        Compares the date the company was first promoted. Companies never
        promoted are not included.
      </Typography>
      <Stack direction="row" spacing={1}>
        <TextField
          select
          size="small"
          label="Operator"
          value={slice.operator}
          onChange={handleOperatorChange}
          sx={{ minWidth: 160 }}
          slotProps={{
            select: {
              SelectDisplayProps: {
                "data-testid": "promotion-date-operator-select",
              } as object,
            },
          }}
        >
          {Object.values(CompanyPromotionDateOperator).map((operator) => (
            <MenuItem
              key={operator}
              value={operator}
              data-testid={`promotion-date-operator-${operator.toLowerCase()}`}
            >
              {OPERATOR_LABELS[operator]}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          type="date"
          size="small"
          label="Promotion date"
          value={slice.date ?? ""}
          onChange={handleDateChange}
          slotProps={{
            inputLabel: { shrink: true },
            htmlInput: { "data-testid": "promotion-date-input" },
          }}
          sx={{ minWidth: 180 }}
        />
      </Stack>
    </Stack>
  );
});

PromotionDateFilter.displayName = "PromotionDateFilter";
